/**
 * EHR Management Component
 *
 * Displays EHRs with facility counts, opens facility list on click,
 * and provides Add EHR flow to connect EHRs to facilities.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import CustomContainer from "../common/CustomContainer";
import ListModal from "../common/ListModal";
import { ALL_EHRS, getEhrFromKey } from "../../constants/ehr-constants";
import { useTheme } from "../../ThemeContext";
import { Button, Dropdown, TextBox } from "@voicecare.ai/ui-library";
import {
  getFacilities,
  getEhrConnectionRequiredFields,
  createEhrConnection,
} from "../../utils/apiClient";

/**
 * Normalize EHR key to canonical form (e.g. Carestack -> CareStack)
 */
const normalizeEhrKey = (key) => {
  if (!key) return "";
  const found = ALL_EHRS.find((e) => e.key.toLowerCase() === String(key).toLowerCase());
  return found ? found.key : key;
};

/**
 * Derive EHR list with facility counts from facilities data
 */
const deriveEhrCountsFromFacilities = (facilities) => {
  const counts = {};
  const ehrToFacilities = {};
  if (!Array.isArray(facilities)) return { counts, ehrToFacilities };
  facilities.forEach((facility) => {
    const mappings = facility.ehrMappings || {};
    Object.keys(mappings).forEach((rawKey) => {
      const key = normalizeEhrKey(rawKey);
      counts[key] = (counts[key] || 0) + 1;
      if (!ehrToFacilities[key]) ehrToFacilities[key] = [];
      ehrToFacilities[key].push(facility);
    });
  });
  return { counts, ehrToFacilities };
};

/**
 * Format field name to label (e.g. customer_key -> Customer Key)
 */
const fieldToLabel = (field) => {
  return field
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const EHRManagement = () => {
  const { themeMode, currentTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ehrCounts, setEhrCounts] = useState({});
  const [ehrToFacilities, setEhrToFacilities] = useState({});
  const [facilitiesModalOpen, setFacilitiesModalOpen] = useState(false);
  const [facilitiesForModal, setFacilitiesForModal] = useState([]);
  const [selectedEhrForModal, setSelectedEhrForModal] = useState("");

  const [addEhrPopupOpen, setAddEhrPopupOpen] = useState(false);
  const [addEhrStep, setAddEhrStep] = useState(1);
  const [requiredFields, setRequiredFields] = useState({});
  const [loadingRequiredFields, setLoadingRequiredFields] = useState(false);
  const [selectedEhrForAdd, setSelectedEhrForAdd] = useState("");
  const [connectionForm, setConnectionForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchFacilities = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFacilities();
      const list = Array.isArray(data) ? data : data?.response || [];
      setFacilities(list);
      const { counts, ehrToFacilities: map } = deriveEhrCountsFromFacilities(list);
      setEhrCounts(counts);
      setEhrToFacilities(map);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      message.error("Failed to load facilities");
      setFacilities([]);
      setEhrCounts({});
      setEhrToFacilities({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  // When redirected from Call Automation with addEhr param, open Add EHR flow with that EHR pre-selected
  useEffect(() => {
    const addEhr = searchParams.get("addEhr");
    if (!addEhr) return;
    const openAddEhrWithPreselection = async () => {
      setAddEhrPopupOpen(true);
      setAddEhrStep(1);
      setLoadingRequiredFields(true);
      try {
        const fields = await getEhrConnectionRequiredFields();
        setRequiredFields(fields || {});
        const ehrKey = Object.keys(fields || {}).find((k) => k.toLowerCase() === addEhr.toLowerCase());
        if (ehrKey) {
          setSelectedEhrForAdd(ehrKey);
          setAddEhrStep(2);
          const fieldList = fields[ehrKey] || [];
          const initial = { facilityId: "" };
          fieldList.forEach((f) => { initial[f] = ""; });
          setConnectionForm(initial);
        }
      } catch (error) {
        console.error("Error fetching required fields:", error);
        message.error("Failed to load EHR configuration");
      } finally {
        setLoadingRequiredFields(false);
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("addEhr");
        return next;
      }, { replace: true });
    };
    openAddEhrWithPreselection();
  }, [searchParams, setSearchParams]);

  const handleEhrCardClick = (ehrKey) => {
    // Show only facilities that HAVE this EHR assigned (matches the count displayed on the card)
    const list = ehrToFacilities[ehrKey] || [];
    setFacilitiesForModal(list);
    setSelectedEhrForModal(ehrKey);
    setFacilitiesModalOpen(true);
  };

  const handleAddEhrClick = async () => {
    setAddEhrPopupOpen(true);
    setAddEhrStep(1);
    setSelectedEhrForAdd("");
    setConnectionForm({});
    try {
      setLoadingRequiredFields(true);
      const fields = await getEhrConnectionRequiredFields();
      setRequiredFields(fields || {});
    } catch (error) {
      console.error("Error fetching required fields:", error);
      message.error("Failed to load EHR configuration");
      setRequiredFields({});
    } finally {
      setLoadingRequiredFields(false);
    }
  };

  const handleEhrSelectForAdd = (ehrKey) => {
    setSelectedEhrForAdd(ehrKey);
    setAddEhrStep(2);
    const fields = requiredFields[ehrKey] || [];
    const initial = { facilityId: "" };
    fields.forEach((f) => { initial[f] = ""; });
    setConnectionForm(initial);
  };

  const handleConnectionFormChange = (field, value) => {
    setConnectionForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitConnection = async () => {
    const facilityId = connectionForm.facilityId;
    if (!facilityId) {
      message.error("Please select a facility");
      return;
    }
    const fields = requiredFields[selectedEhrForAdd] || [];
    const missing = fields.filter((f) => !connectionForm[f]);
    if (missing.length) {
      message.error(`Please fill required fields: ${missing.map(fieldToLabel).join(", ")}`);
      return;
    }
    const payload = {
      facilityId,
      ehrName: selectedEhrForAdd,
    };
    fields.forEach((f) => {
      if (f !== "facilityId" && connectionForm[f]) payload[f] = connectionForm[f];
    });
    try {
      setSubmitting(true);
      await createEhrConnection(payload);
      message.success("EHR connection created successfully");
      setAddEhrPopupOpen(false);
      setAddEhrStep(1);
      setSelectedEhrForAdd("");
      setConnectionForm({});
      fetchFacilities();
    } catch (error) {
      console.error("Error creating EHR connection:", error);
      message.error(error?.response?.data?.message || "Failed to create EHR connection");
    } finally {
      setSubmitting(false);
    }
  };

  const sortEhrKeysByType = (keys) => {
    const typePriority = { medical: 0, dental: 1 };
    return [...keys].sort((a, b) => {
      const aMeta = ALL_EHRS.find((e) => e.key.toLowerCase() === a.toLowerCase());
      const bMeta = ALL_EHRS.find((e) => e.key.toLowerCase() === b.toLowerCase());
      const aTypeRank = aMeta && typePriority[aMeta.type] !== undefined ? typePriority[aMeta.type] : 2;
      const bTypeRank = bMeta && typePriority[bMeta.type] !== undefined ? typePriority[bMeta.type] : 2;
      if (aTypeRank !== bTypeRank) return aTypeRank - bTypeRank;
      const aLabel = (aMeta?.displayName || aMeta?.key || a || "").toLowerCase();
      const bLabel = (bMeta?.displayName || bMeta?.key || b || "").toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  };
  
  const ehrsWithCounts = Object.keys(ehrCounts).length
    ? sortEhrKeysByType(Object.keys(ehrCounts))
    : [];
  const hasMedicalEhrs = ehrsWithCounts.some((key) => getEhrFromKey(key).type === "medical");
  const hasDentalEhrs = ehrsWithCounts.some((key) => getEhrFromKey(key).type === "dental");
  const shouldShowTypeBreak = hasMedicalEhrs && hasDentalEhrs;

  const ehrCardsForAdd = Object.keys(requiredFields).length
    ? sortEhrKeysByType(Object.keys(requiredFields))
    : [];

  
  const facilityOptions = React.useMemo(() => {
    const list = selectedEhrForAdd
      ? facilities.filter((f) => {
          const mappings = f.ehrMappings || {};
          const hasEhr = Object.keys(mappings).some((k) => normalizeEhrKey(k) === selectedEhrForAdd);
          return !hasEhr;
        })
      : facilities;
    return list.map((f) => ({
      label: f.facilityName || f._id,
      value: f._id,
    }));
  }, [facilities, selectedEhrForAdd]);

  const cardBaseStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.875rem",
    padding: "1rem .5rem",
    borderRadius: "0.75rem",
    border: "0.0625rem solid #e2e8f0",
    backgroundColor: themeMode === "dark" ? "#1a1a2e" : "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: themeMode === "dark"
      ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)"
      : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)",
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(0.25rem)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  };

  const modalStyle = {
    backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
    color: currentTheme?.homePage?.textColor || "#333",
    borderRadius: "1rem",
    padding: "2rem",
    maxWidth: "55rem",
    width: "95%",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: themeMode === "dark"
      ? "0 1.25rem 2.5rem rgba(0, 0, 0, 0.6)"
      : "0 1.25rem 2.5rem rgba(0, 0, 0, 0.15)",
    border: themeMode === "dark" ? "0.0625rem solid #302d4c" : "0.0625rem solid #e9e8ea",
  };

  return (
    <CustomContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: ".5rem",
          width: "100%",
          flex: "0 0 auto",
        }}
      >
        {/* <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: currentTheme?.homePage?.textColor || "#333",
            margin: 0,
          }}
        >
          EHR Management
        </h1> */}
        <Button
          label="Add EHR"
          onClick={handleAddEhrClick}
          style={{
            backgroundColor: currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47",
            borderColor: currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47",
            marginLeft: "auto",
            flexShrink: 0,
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: currentTheme?.homePage?.textColor, flex: "0 0 auto" }}>
          Loading...
        </div>
      ) : ehrsWithCounts.length === 0 ? (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            color: themeMode === "dark" ? "#a0a0a0" : "#666",
            fontSize: "0.875rem",
            flex: "0 0 auto",
          }}
        >
          No EHRs configured yet. Click &quot;Add EHR&quot; to connect an EHR to a facility.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              ehrsWithCounts.length <= 2
                ? `repeat(${ehrsWithCounts.length}, minmax(12rem, 14rem))`
                : "repeat(4, minmax(0, 1fr))",
            gap: "3.5rem",
            justifyContent: "start",
            alignContent: "start",
            flex: "0 0 auto",
          }}
        >
          {ehrsWithCounts.map((ehrKey, index) => {
            const ehr = getEhrFromKey(ehrKey);
            const count = ehrCounts[ehrKey] || 0;
            const prevKey = index > 0 ? ehrsWithCounts[index - 1] : null;
            const prevType = prevKey ? getEhrFromKey(prevKey).type : ehr.type;
            const isTypeBreak = shouldShowTypeBreak && index > 0 && ehr.type !== prevType;
            return (
              <div key={ehrKey} style={{ display: "contents" }}>
                {isTypeBreak && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      height: "1.5rem",
                    }}
                  />
                )}
                <div
                  onClick={() => handleEhrCardClick(ehrKey)}
                  style={cardBaseStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47";
                    e.currentTarget.style.transform = "translateY(-0.125rem)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ height: "4rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={ehr.logo}
                      alt={ehr.displayName || ehrKey}
                      style={{
                        maxWidth: ehr.magnifyby ? `${9 * (1 + ehr.magnifyby / 100)}rem` : "9rem",
                        maxHeight: ehr.magnifyby ? `${4.75 * (1 + ehr.magnifyby / 100)}rem` : "4.75rem",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: themeMode === "dark" ? "#a0a0a0" : "#64748b",
                    }}
                  >
                    {count} {count === 1 ? "facility" : "facilities"} assigned
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ListModal
        open={facilitiesModalOpen}
        onClose={() => setFacilitiesModalOpen(false)}
        title={`Facilities - ${getEhrFromKey(selectedEhrForModal).displayName?.trim() || getEhrFromKey(selectedEhrForModal).key || selectedEhrForModal}`}
        subtitle={`${facilitiesForModal.length} facility/facilities with this EHR assigned`}
        data={facilitiesForModal}
        renderItem={(facility) => (
          <div>
            <strong>{facility.facilityName}</strong>
            {facility.location && (
              <div style={{ fontSize: "0.875rem", marginTop: "0.25rem", opacity: 0.85 }}>
                {[
                  facility.location.streetAddress,
                  facility.location.city,
                  facility.location.state,
                  facility.location.zipcode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
          </div>
        )}
        maxWidth={560}
      />

      {addEhrPopupOpen && (
        <div style={overlayStyle} onClick={() => !submitting && setAddEhrPopupOpen(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "600" }}>
                  {addEhrStep === 1 ? "Add EHR Connection" : "Configure Connection"}
                </h2>
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.875rem", opacity: 0.8 }}>
                  {addEhrStep === 1 ? "Select an EHR to continue" : `${selectedEhrForAdd} - Fill required fields`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !submitting && setAddEhrPopupOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  opacity: 0.7,
                }}
              >
                ×
              </button>
            </div>

            {addEhrStep === 1 && (
              <div>
                {loadingRequiredFields ? (
                  <div style={{ padding: "2rem", textAlign: "center" }}>Loading EHRs...</div>
                ) : ehrCardsForAdd.length === 0 ? (
                  <div style={{ padding: "2rem", textAlign: "center", opacity: 0.8 }}>
                    No EHRs available for configuration.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: "0.75rem",
                      marginTop: "1.25rem",
                    }}
                  >
                    {ehrCardsForAdd.map((ehrKey, index) => {
                      const ehr = getEhrFromKey(ehrKey);
                      const prevKey = index > 0 ? ehrCardsForAdd[index - 1] : null;
                      const prevType = prevKey ? getEhrFromKey(prevKey).type : ehr.type;
                      const isTypeBreak = index > 0 && ehr.type !== prevType;
                      return (
                        <div key={ehrKey} style={{ display: "contents" }}>
                          {isTypeBreak && (
                            <div
                              style={{
                                gridColumn: "1 / -1",
                                height: "1.5rem",
                              }}
                            />
                          )}
                          <div
                            onClick={() => handleEhrSelectForAdd(ehrKey)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.875rem",
                              padding: "1.5rem 1rem",
                              borderRadius: "0.75rem",
                              border: "0.0625rem solid #e2e8f0",
                              backgroundColor: "#ffffff",
                              cursor: "pointer",
                              opacity: 1,
                              filter: "none",
                              transition: "all 0.2s ease",
                              boxShadow:
                                themeMode === "dark"
                                  ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)"
                                  : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)",
                              position: "relative",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor =
                                currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47";
                              e.currentTarget.style.boxShadow =
                                themeMode === "dark"
                                  ? "0 0.25rem 1rem rgba(0, 0, 0, 0.4)"
                                  : "0 0.25rem 1rem rgba(0, 0, 0, 0.12)";
                              e.currentTarget.style.transform = "translateY(-0.125rem)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e2e8f0";
                              e.currentTarget.style.boxShadow =
                                themeMode === "dark"
                                  ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)"
                                  : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "3.75rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "transparent",
                                border: "none",
                                borderRadius: "0.5rem",
                                padding: "0.625rem 1rem",
                              }}
                            >
                            <img
                              src={ehr.logo}
                              alt={ehr.displayName || ehrKey}
                              style={{
                                maxWidth: ehr.magnifyby
                                  ? `${9 * (1 + ehr.magnifyby / 100)}rem`
                                  : "9rem",
                                maxHeight: ehr.magnifyby
                                  ? `${4.75 * (1 + ehr.magnifyby / 100)}rem`
                                  : "4.75rem",
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                          {ehr.displayName && ehr.displayName.trim() && (
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: "600",
                                color: "#1e293b",
                                textAlign: "center",
                                lineHeight: "1.3",
                              }}
                            >
                              {ehr.displayName.trim()}
                            </span>
                          )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {addEhrStep === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "0.375rem", fontSize: "0.875rem", fontWeight: "600" }}>
                    Facility *
                  </label>
                  <Dropdown
                    options={facilityOptions}
                    value={facilityOptions.find((o) => o.value === connectionForm.facilityId) || null}
                    onChange={(opt) => handleConnectionFormChange("facilityId", opt?.value || "")}
                    placeholder="Select facility"
                    themeMode={themeMode}
                    isSearchable
                    width="100%"
                  />
                </div>
                {(requiredFields[selectedEhrForAdd] || []).map((field) => (
                    <div key={field}>
                      <label style={{ display: "block", marginBottom: "1rem", fontSize: "0.875rem", fontWeight: "600" }}>
                        {fieldToLabel(field)} *
                      </label>
                      <TextBox
                        type={field.toLowerCase().includes("password") ? "password" : "text"}
                        value={connectionForm[field] || ""}
                        onChange={(e) => handleConnectionFormChange(field, e.target.value)}
                        placeholder={`Enter ${fieldToLabel(field)}`}
                        style={{ width: "98%", marginBottom: "1rem", borderRadius: "0.5rem", padding: "0.5rem" }}
                      />
                    </div>
                  ))}
                <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
                  <Button
                    label="Back"
                    onClick={() => {
                      setAddEhrStep(1);
                      setSelectedEhrForAdd("");
                      setConnectionForm({});
                    }}
                    disabled={submitting}
                  />
                  <Button
                    label={submitting ? "Submitting..." : "Submit"}
                    onClick={handleSubmitConnection}
                    disabled={submitting}
                    style={{
                      backgroundColor: currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47",
                      borderColor: currentTheme?.callAutomationPage?.activeButtonColor || "#FF6B47",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </CustomContainer>
  );
};

export default EHRManagement;
